import { parseEntityRef } from '@backstage/catalog-model';
import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';

import {
  FeatureTree,
  QualityScore,
  QualityScoreHistoryEntry,
  QualityScoreSummary,
} from '@internal/backstage-plugin-scores-common';

import { QualityScoresApi } from './QualityScoresApi';

/**
 * Every row of the scores table asks for the score list, so one fetch is shared for
 * this long. Scores change at most once per scheduled run.
 */
const LIST_TTL_MS = 60_000;

function entityPath(entityRef: string): string {
  const { kind, namespace, name } = parseEntityRef(entityRef);
  return [kind, namespace, name]
    .map(part => encodeURIComponent(part.toLocaleLowerCase('en-US')))
    .join('/');
}

export class QualityScoresClient implements QualityScoresApi {
  private baseUrl?: Promise<string>;
  private list?: { fetchedAt: number; promise: Promise<QualityScoreSummary[]> };

  private constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  public static create({
    discoveryApi,
    fetchApi,
  }: {
    discoveryApi: DiscoveryApi;
    fetchApi: FetchApi;
  }): QualityScoresClient {
    return new QualityScoresClient(discoveryApi, fetchApi);
  }

  private getBaseUrl(): Promise<string> {
    return (this.baseUrl ??= this.discoveryApi.getBaseUrl('scores'));
  }

  public listLatest(): Promise<QualityScoreSummary[]> {
    if (!this.list || Date.now() - this.list.fetchedAt > LIST_TTL_MS) {
      const promise = this.fetch<QualityScoreSummary[]>('/entities');
      // Don't cache a failure; the next caller retries.
      promise.catch(() => {
        if (this.list?.promise === promise) this.list = undefined;
      });
      this.list = { fetchedAt: Date.now(), promise };
    }
    return this.list.promise;
  }

  public getLatest(entityRef: string): Promise<QualityScore | null> {
    return this.fetchOrNull<QualityScore>(`/entities/${entityPath(entityRef)}`);
  }

  public getHistory(
    entityRef: string,
    options: { limit?: number; since?: string } = {},
  ): Promise<QualityScoreHistoryEntry[]> {
    const query = new URLSearchParams();
    if (options.limit !== undefined) query.set('limit', String(options.limit));
    if (options.since) query.set('since', options.since);
    const suffix = query.toString() ? `?${query}` : '';
    return this.fetch<QualityScoreHistoryEntry[]>(
      `/entities/${entityPath(entityRef)}/history${suffix}`,
    );
  }

  public getFeatureTree(entityRef: string): Promise<FeatureTree | null> {
    return this.fetchOrNull<FeatureTree>(
      `/entities/${entityPath(entityRef)}/feature-tree`,
    );
  }

  private async fetchOrNull<T>(path: string): Promise<T | null> {
    try {
      return await this.fetch<T>(path);
    } catch (error) {
      if (error instanceof ResponseError && error.statusCode === 404) {
        return null;
      }
      throw error;
    }
  }

  private async fetch<T>(path: string): Promise<T> {
    const response = await this.fetchApi.fetch(
      `${await this.getBaseUrl()}${path}`,
    );
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    return (await response.json()) as T;
  }
}
